import { useReducer, useState } from 'react';
import { FieldType, validateField, validateForm } from '@common/util/validateForm';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type UseFormValue = string | number | boolean | any;

export interface UseFormSchemaOptions {
  type: FieldType;
  required?: boolean;
  invalidCharacters?: string[];
  allowedCharacters?: string | RegExp;
  checkAgainst?: string;
  trueValue?: UseFormValue;
  falseValue?: UseFormValue;
  minValue?: number;
  maxValue?: number;
  minLength?: number;
  maxLength?: number;
  minRequired?: number;
  maxAllowed?: number;
  acceptedValues?: {
    label: string;
    value: UseFormValue;
  }[];
  criteria?: {
    specialChars: boolean;
    number: boolean;
    upperAndLower: boolean;
  }
  restrictUnicode?: boolean;
}

export interface UseFormSchema {
  [key: string]: {
    value?: UseFormValue;
    schema: UseFormSchemaOptions;
  }
}

export interface UseForm {
  schema: UseFormSchema;
  data: { [key: string]: UseFormValue };
  errors: { [key: string]: string };
  hasErrors: boolean;
  setField: (key: string, value: UseFormValue) => void;
  resetForm: () => void;
  setErrors: (errors: string[]) => void;
  setData: (data: { [key: string]: UseFormValue }) => void;
  resetErrors: (softReset?: boolean) => void;
  validateField: (key: string, rawValue: UseFormValue) => void;
  validate: () => void;
}

function formReducer (state, { newState, key, value }: { newState?: {[key: string]: UseFormValue}, key?: string, value?: UseFormValue }) {
  if (newState) {
    return newState;
  }

  return {
    ...state,
    [ key ]: value
  };
}

function stateFromSchema (schema: UseFormSchema) {
  return Object.keys(schema).reduce((state, key) => {
    state[ key ] = typeof schema[ key ].value === 'undefined'
      ? ''
      : schema[ key ].value;
    return state;
  }, {});
}

function errorsFromSchema (schema: UseFormSchema) {
  return Object.keys(schema).reduce((state, key) => {
    state[ key ] = false;
    return state;
  }, {});
}

function checkForErrors (errors: Record<string, unknown>) {
  const keys = Object.keys(errors);
  
  let key;
  while (key = keys.pop()) {
    if (errors[ key ]) return true;
  }

  return false;
}

export function useForm (schema: UseFormSchema) {
  const [ data, dispatchForm ] = useReducer(formReducer, stateFromSchema(schema));
  const [ errors, dispatchError ] = useReducer(formReducer, errorsFromSchema(schema));
  const [ hasErrors, setHasErrors ] = useState(false);

  const resetForm = () => {
    setHasErrors(false);
    dispatchForm({ newState: stateFromSchema(schema) });
  };

  const setErrors = (errors: {}) => {
    setHasErrors(checkForErrors(errors));
    dispatchError({ newState: errors });
  };
  
  const resetErrors = (softReset?: boolean) => {
    setHasErrors(false);
    if (!softReset) dispatchError({ newState: errorsFromSchema(schema) });
  };

  const setField = (key: string, value: UseFormValue) => {
    dispatchForm({ key, value });
  };

  const setFormData = (data: { [key: string]: UseFormValue }) => {
    setHasErrors(false);
    Object.keys(data).forEach((key) => {
      setField(key, data[ key ]);
    });
  };

  const _validateField = (key: string, rawValue: UseFormValue) => {
    let result;
    if (schema[ key ].schema.type === FieldType.PASSWORD_VERIFY) {
      const otherKey = schema[ key ].schema.checkAgainst;
      result = validateField(rawValue, data[ otherKey ], schema[ key ].schema);
    } else {
      result = validateField(rawValue, schema[ key ].schema);
    }

    const { error, value } = result;
    setHasErrors(!!error);

    dispatchForm({ key, value });
    dispatchError({ key, value: error });
  };

  const validate = <T, >() => {
    const { errors, values } = validateForm(data, schema);
    const hasErrors = checkForErrors(errors);
    setHasErrors(hasErrors);

    dispatchForm({ newState: values });
    dispatchError({ newState: errors });

    return { errors, data: values as T, hasErrors };
  };

  return {
    data,
    setData: setFormData,
    errors,
    schema,
    hasErrors,
    setField,
    resetForm,
    setErrors,
    resetErrors,
    validateField: _validateField,
    validate
  };
}
